import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { DesignLogo } from "../../../core/models/entities/Design";
import { useOrderActionHandler } from "../../screens/order-create/hooks/useOrderActionsHandler";
import { useCartContext } from "../../../contexts/CartContext";
import {
  BaseLogo,
  getLogoColors,
  isDigital,
} from "../../../core/models/entities/BaseLogo";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { CartItemLogo } from "../../screens/order-create/models/CartItem";
import { LogoQuantitySelector } from "../../screens/order-create/subcomponents/LogoQuantitySelector";
import { LogoDimensions } from "../Logo/LogoDimensions";
import { useLogoContext } from "../../screens/design-editor2/contexts/logo-options.context";
import { DigitalColorElement } from "../Logo/DigitalColorElement";
import { VectorColorElement } from "../Logo/VectorColorElement";
import { AddToCartIcon } from "../../../resources/icons/Icons";
import { useCartHidden } from "../../../hooks/useCartHidden";

interface useAddLogoToOrderDialogProps {
  component: JSX.Element;
  open: () => void;
}

export function useAddLogoToOrderDialog(
  designLogo?: DesignLogo,
  baseLogo?: BaseLogo,
  onAccept?: () => void,
  onCancel?: () => void
): useAddLogoToOrderDialogProps {
  const dialog = useDialog();

  function handleAccept() {
    if (onAccept) onAccept();
    dialog.closeDialog();
  }

  function handleCancel() {
    if (onCancel) onCancel();
    dialog.closeDialog();
  }

  return {
    component: (
      <AddLogoToDesignOrderDialog
        dialogVm={dialog}
        baseLogo={baseLogo ?? designLogo?.logo}
        onAccept={handleAccept}
        onCancel={handleCancel}
      />
    ),
    open: dialog.openDialog,
  };
}

interface AddLogoToDesignOrderDialogProps {
  dialogVm: Dialog_VM;
  baseLogo?: BaseLogo;
  onAccept: () => void;
  onCancel?: () => void;
}

export function AddLogoToDesignOrderDialog({
  baseLogo,
  onAccept,
  onCancel,
  dialogVm,
}: AddLogoToDesignOrderDialogProps) {
  const { t } = useTranslation();
  const { items, removeItem } = useCartContext();
  const { handleLogoSetQuantities } = useOrderActionHandler();
  const { logos } = useCartContext();
  const cartHidden = useCartHidden();

  if (!baseLogo) return null;

  const selectedOrderLine = items.find((i) => i.id === baseLogo.id.toString());
  const logo = logos.find((x) => x.id === "" + baseLogo.id);
  const isNumeric = baseLogo.logoType === "NumberLogo";

  // Keep the old state to ensure logo quantities can be reverted to original state.
  // Never update this state. It should not be a state.
  const [logosToStorage, _setLogosToStorage] = useState<number>(
    logo?.toStorage ?? 0
  );
  const [logosToDelivery, _setLogosToDelivery] = useState<number>(
    logo?.toDelivery ?? 0
  );

  // HACK:
  // Because the LogoQuantitySelector component handles quantity state internally,
  //  we either have to let it do that or refactor the component and likely the architecture around other usages.
  // Luckily, this state is stored synchronously in localStorage,
  //  so instead of refactoring now, we can just revert the quantity state afterwards if the user cancels.
  // It would be prudent to refactor this mess instead of parsing the code and trying to update it.
  // This is the "cancel" option.
  const revert = () => {
    if (selectedOrderLine != null)
      handleLogoSetQuantities(baseLogo, logosToDelivery, logosToStorage);
    if (baseLogo != null) removeItem(baseLogo.id.toString());
    onCancel?.call(null);
  };

  // HACK:
  // In case the user confirms the choices, we don't need to do anything,
  //  because the LogoQuantitySelector component has already handled the state update internally.
  // This is the "confirm" option.
  const doNotRevert = () => {
    onAccept?.call(null);
  };

  const number = baseLogo.product?.sku;
  const storage = baseLogo.inStorage ?? 0;

  // Get colors with names like in logo list item
  const { options } = useLogoContext();
  const colorOptions = options.colors;
  const colorsEmbroidery = options.colorsEmbroidery;
  const colors = getLogoColors(baseLogo, colorOptions, colorsEmbroidery);

  const colorsElements = isDigital(baseLogo) ? (
    <DigitalColorElement />
  ) : (
    <div className="grid grid-rows-4 grid-flow-col">
      {colors.map((co) =>
        co.isIndigo ? (
          <DigitalColorElement key={co.id} showText={true} text={co.name} />
        ) : (
          <VectorColorElement key={co.id} showName={true} color={co} />
        )
      )}
    </div>
  );

  const alt = t("compDialogsInput.logoAlt", { title: baseLogo.title });
  const src = baseLogo.displayImage.lowResUrl;

  // For name logos, "Add to Order" only makes sense once at least one name
  // with a quantity has been entered. The selector syncs its rows to the cart
  // on every edit, so the cart order line is the single source of truth here.
  const isNameLogo = baseLogo.logoType === "NameLogo";
  const namedRows = isNameLogo
    ? ((selectedOrderLine as CartItemLogo)?.deliveryQuantities ?? []).filter(
        (row) => (row.label ?? "").trim() !== "" && (row.quantity ?? 0) > 0
      )
    : [];
  const addToOrderDisabled = isNameLogo && namedRows.length === 0;

  return (
    <DialogFrame dialogOption={dialogVm} onClose={revert}>
      <div className={"flex flex-col space-y-8 p-4"}>
        <p className={"text-left text-base font-bold"}>
          {t("compDialogsInput.addToOrderWithTitle", { title: baseLogo.title })}
        </p>
        <div className="flex flex-row">
          <div className={"flex flex-col mr-8 space-y-2"}>
            <div className="flex flex-row justify-between min-h-[100px] min-w-[300px] mb-4">
              <img
                className={"w-[200px] h-[100px] object-contain mt-4"}
                src={src}
                alt={alt}
              />
            </div>

            <div className="flex flex-row space-x-1">
              <p className="uppercase font-bold">
                {t("compDialogsInput.numberAbbrev")}
              </p>
              <p>{number}</p>
            </div>
            <div className="flex flex-row space-x-1">
              <p className="uppercase font-bold">{t("type")}</p>
              <div className="flex capitalize">
                <p>{baseLogo.transferMethod.type.toLowerCase()}</p>
              </div>
            </div>
            <div className="flex flex-row space-x-1">
              <p className="uppercase font-bold">
                {t("compDialogsInput.method")}
              </p>
              <div className="flex capitalize">
                <p>{baseLogo.transferMethod.displayName.toLowerCase()}</p>
              </div>
            </div>

            <div className="flex flex-row space-x-1">
              <p className="uppercase font-bold">
                {t("compDialogsInput.size")}
              </p>
              <LogoDimensions logo={baseLogo} format={"WWxYYmm"} />
            </div>
            <div className="flex flex-row space-x-1">
              <p className="uppercase font-bold">
                {t("compDialogsInput.color")}
              </p>
              {colorsElements}
            </div>

            <div className={"flex flex-row justify-between min-h-[100px]"}>
              {/* <LogoStorage logo={baseLogo} /> */}
              {/* <div className="flex-1 min-w-[150px]"> */}
              {/*   <p className='uppercase font-bold'>In Storage</p> */}
              {/*   <p>{storage}</p> */}
              {/* </div> */}
            </div>
          </div>

          <div className={`grid gap-4 ${isNumeric ? "grid-cols-2" : ""}`}>
            <LogoQuantitySelector
              logo={baseLogo}
              orderLine={selectedOrderLine as CartItemLogo}
            />
          </div>
        </div>

        <div className={"flex flex-col gap-1"}>
          <div className={"flex flex-row justify-between"}>
            <SecondaryButton
              width="md"
              className={"bg-[#F8FAFD]"}
              onClick={revert}
            >
              <span className={"flex-1"}>{t("cancel")}</span>
            </SecondaryButton>
            {!cartHidden && (
              <PrimaryButton
                width="md"
                className={"bg-[#F8FAFD] px-4"}
                onClick={doNotRevert}
                disabled={addToOrderDisabled}
              >
                <AddToCartIcon />
                <span className={"flex-1"}>{t("compDialogsInput.addToOrder")}</span>
              </PrimaryButton>
            )}
          </div>
          {!cartHidden && addToOrderDisabled && (
            <p className={"text-xs text-gray-500 text-right"}>
              {t("compDialogsInput.addNameToEnableOrder")}
            </p>
          )}
        </div>
      </div>
    </DialogFrame>
  );
}
